import React, { useState } from 'react';
import { WeighingTicketData } from '@/components/WeighingTicket';
import { Ticket } from '@/services/ticketsService';
import { generatePrintHTML, generatePDF, generateEmailTemplate } from '@/utils/ticketUtils';
import { convertPeseeToTicketData } from '@/utils/ticketDataConverter';
import EmailModal from './EmailModal';

interface TicketActionsProps {
  ticketData?: WeighingTicketData;
  ticket?: Ticket;
  onPrint?: () => void;
  onExport?: () => void;
  onEmail?: () => void;
  className?: string;
  showLabels?: boolean;
  variant?: 'default' | 'compact' | 'minimal';
}

const TicketActions: React.FC<TicketActionsProps> = ({
  ticketData,
  ticket,
  onPrint,
  onExport,
  onEmail,
  className = '',
  showLabels = true,
  variant = 'default'
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: '',
    message: '',
    cc: '',
    bcc: ''
  });

  // Déterminer les données du ticket à utiliser - Utilise la fonction centralisée
  const currentTicketData = ticketData || (ticket ? convertPeseeToTicketData(ticket as any) : undefined);

  const handlePrint = async () => {
    if (!currentTicketData) return;
    
    try {
      const printHTML = generatePrintHTML(currentTicketData);
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        alert('Impossible d\'ouvrir la fenêtre d\'impression. Veuillez autoriser les popups.');
        return;
      }

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
      
      onPrint?.();
    } catch (error) {
      console.error('❌ Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  const handleExport = async () => {
    if (!currentTicketData) return;
    
    try {
      await generatePDF(currentTicketData);
      onExport?.();
    } catch (error) {
      console.error('❌ Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
  };

  const handleEmail = () => {
    if (!currentTicketData) return;
    
    setEmailData({
      recipients: '',
      subject: `Ticket de Pesée - ${currentTicketData.code}`,
      message: generateEmailTemplate(currentTicketData),
      cc: '',
      bcc: ''
    });
    setShowEmailModal(true);
    onEmail?.();
  };

  const handleEmailSent = () => {
    setShowEmailModal(false);
  };

  if (!currentTicketData) {
    return null;
  }

  const baseClasses = "flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const getVariantClasses = () => {
    switch (variant) {
      case 'compact':
        return {
          button: "px-3 py-2 text-sm rounded-lg",
          icon: "mr-1",
          text: showLabels ? "text-xs" : "hidden"
        };
      case 'minimal':
        return {
          button: "p-2 rounded-lg",
          icon: "mr-0",
          text: "hidden"
        };
      default:
        return {
          button: "px-4 py-2 rounded-lg",
          icon: "mr-2",
          text: showLabels ? "text-sm" : "hidden"
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <>
      <div className={`flex space-x-2 ${className}`}>
        <button
          onClick={handlePrint}
          className={`${baseClasses} ${variantClasses.button} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500`}
          title="Imprimer le ticket"
        >
          <i className={`fas fa-print ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Imprimer</span>
        </button>

        <button
          onClick={handleExport}
          className={`${baseClasses} ${variantClasses.button} bg-green-600 hover:bg-green-700 text-white focus:ring-green-500`}
          title="Exporter en PDF"
        >
          <i className={`fas fa-file-pdf ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Exporter PDF</span>
        </button>

        <button
          onClick={handleEmail}
          className={`${baseClasses} ${variantClasses.button} bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500`}
          title="Envoyer par email"
        >
          <i className={`fas fa-envelope ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Email</span>
        </button>
      </div>

      {showEmailModal && (
        <EmailModal
          ticketData={currentTicketData}
          emailData={emailData}
          onEmailDataChange={setEmailData}
          onSend={handleEmailSent}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </>
  );
};

export default TicketActions;
